'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCentrifugo } from '@/contexts/CentrifugoContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useProfileData } from '@/queries/useProfileQueries';
import { useIsAuthed } from '@/hooks/useIsAuthed';
import { queryKeys } from '@/lib/queryClient';
import { showCustomToast } from '@/components/Toast';
import { RainToast } from '@/components/Rain/RainToast';
import { arrayField, literalField, nullableStringField, numberField, rawField, stringField } from '@/lib/jsonBody';
import type { CurrentRain, RainCase, RainDetail, RainItem } from '@/services/generated/Api';

const getRainChannel = (prefix: string): string => (prefix ? `${prefix}_rains` : 'rains');

interface RainRealtimeWinner {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  level: number;
  item: RainItem;
}

const EVENT_TYPES = ['rain_started', 'participants', 'rain_completed'] as const;

interface RainRealtimeEvent {
  type: (typeof EVENT_TYPES)[number];
  rainId: string;
  case?: RainCase;
  startAt?: string;
  endAt?: string;
  winnersCount?: number;
  participantsCount?: number;
  winners?: RainRealtimeWinner[];
}

const parseCase = (value: unknown): RainCase | undefined => {
  const id = stringField(value, 'id');
  const name = stringField(value, 'name');
  const imageUrl = stringField(value, 'imageUrl');
  const price = numberField(value, 'price');
  if (id === undefined || name === undefined || imageUrl === undefined || price === undefined) {
    return undefined;
  }
  return { id, name, imageUrl, imagePlaceholder: nullableStringField(value, 'imagePlaceholder'), price };
};

const parseItem = (value: unknown): RainItem | null => {
  const hashName = stringField(value, 'hashName');
  const name = stringField(value, 'name');
  const priceInCredits = numberField(value, 'priceInCredits');
  if (hashName === undefined || name === undefined || priceInCredits === undefined) return null;
  return {
    hashName,
    name,
    imageUrl: nullableStringField(value, 'imageUrl'),
    rarityColor: nullableStringField(value, 'rarityColor'),
    priceInCredits,
  };
};

const parseWinner = (value: unknown): RainRealtimeWinner | null => {
  const userId = stringField(value, 'userId');
  const level = numberField(value, 'level');
  const item = parseItem(rawField(value, 'item'));
  if (userId === undefined || level === undefined || item === null) return null;
  return {
    userId,
    firstName: nullableStringField(value, 'firstName'),
    lastName: nullableStringField(value, 'lastName'),
    level,
    item,
  };
};

const isWinner = (winner: RainRealtimeWinner | null): winner is RainRealtimeWinner => winner !== null;

const parseEvent = (data: unknown): RainRealtimeEvent | null => {
  const type = literalField(data, 'type', EVENT_TYPES);
  const rainId = stringField(data, 'rainId');
  if (type === undefined || rainId === undefined) return null;
  return {
    type,
    rainId,
    case: parseCase(rawField(data, 'case')),
    startAt: stringField(data, 'startAt'),
    endAt: stringField(data, 'endAt'),
    winnersCount: numberField(data, 'winnersCount'),
    participantsCount: numberField(data, 'participantsCount'),
    winners: arrayField(data, 'winners')?.map(parseWinner).filter(isWinner),
  };
};

const RAIN_TOAST_MS = 8000;

export const useRainSubscription = (enabled = true) => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();
  const isAuthed = useIsAuthed();
  const profile = useProfileData();
  const userId = profile?.id;

  const channelName = useMemo(() => getRainChannel(channelPrefix), [channelPrefix]);

  const navigation = useNavigation();
  const navRef = useRef(navigation);
  navRef.current = navigation;

  const handleEvent = useCallback(
    (data: unknown) => {
      const event = parseEvent(data);
      if (!event) return;

      switch (event.type) {
        case 'rain_started': {
          const sourceCase = event.case;
          const { startAt, endAt } = event;
          if (!sourceCase || !endAt) return;

          if (startAt) {
            queryClient.setQueryData<CurrentRain | null>(queryKeys.rainCurrent, (prev) => ({
              id: event.rainId,
              startAt,
              endAt,
              winnersCount: event.winnersCount ?? 0,
              participantsCount: event.participantsCount ?? 0,
              isParticipating: false,
              canJoin: prev?.canJoin ?? true,
              joinBlockedReason: prev?.joinBlockedReason,
              minLevel: prev?.minLevel ?? 0,
              case: sourceCase,
            }));
          }
          queryClient.invalidateQueries({ queryKey: queryKeys.rainCurrent });

          const minutesLeft = Math.max(0, Math.floor((new Date(endAt).getTime() - Date.now()) / 60_000));
          showCustomToast(
            (toastId) => (
              <RainToast
                kind="start"
                sourceCase={sourceCase}
                minutesLeft={minutesLeft}
                onClick={() => navRef.current.navigate('rain-details', { rainId: event.rainId })}
                onDismiss={() => toast.dismiss(toastId)}
              />
            ),
            { id: `rain-start-${event.rainId}`, duration: RAIN_TOAST_MS },
          );
          return;
        }
        case 'participants': {
          const count = event.participantsCount;
          if (count === undefined) return;
          queryClient.setQueryData<CurrentRain | null>(queryKeys.rainCurrent, (prev) =>
            prev && prev.id === event.rainId ? { ...prev, participantsCount: count } : prev,
          );
          queryClient.setQueryData<RainDetail | null>(queryKeys.rainDetail(event.rainId), (prev) =>
            prev ? { ...prev, participantsCount: count } : prev,
          );
          return;
        }
        case 'rain_completed': {
          queryClient.invalidateQueries({ queryKey: queryKeys.rainCurrent });
          queryClient.invalidateQueries({ queryKey: queryKeys.rainHistory });
          queryClient.invalidateQueries({ queryKey: queryKeys.rainLast });
          queryClient.invalidateQueries({ queryKey: queryKeys.rainDetail(event.rainId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.rainParticipants(event.rainId) });

          const mine = userId ? event.winners?.find((w) => w.userId === userId) : undefined;
          if (!mine) return;

          showCustomToast(
            (toastId) => (
              <RainToast
                kind="win"
                prize={mine.item}
                onClick={() => navRef.current.navigate('rain-details', { rainId: event.rainId })}
                onDismiss={() => toast.dismiss(toastId)}
              />
            ),
            { id: `rain-win-${event.rainId}`, duration: RAIN_TOAST_MS },
          );
          return;
        }
      }
    },
    [queryClient, userId],
  );

  useEffect(() => {
    if (!enabled || !isAuthed || !isConnected || !channelName) return;
    const unsubscribe = subscribe(channelName, handleEvent);
    return unsubscribe;
  }, [enabled, isAuthed, isConnected, channelName, subscribe, handleEvent]);

  return { isConnected };
};
